import { createContext, useState, useCallback, useRef } from 'react'

import { mediaAssetGroupsUrl, filterUrl } from 'src/configs/api'
import { useAPI } from 'src/utils/api'

export const MediaLibraryContext = createContext()

export const MediaLibraryProvider = ({ children }) => {
  const [mediaListData, setMediaListData] = useState([])
  const [selectedMedia, setSelectedMedia] = useState(null)
  const [loading, setLoading] = useState(true)
  const [totalMedia, setTotalMedia] = useState(0)
  // Keyed by the real backend AssetType values — there is no 'other' bucket;
  // the "Others" tab in the UI is a client-side sum of document + audio.
  const [mediaTypeCounts, setMediaTypeCounts] = useState({ image: 0, video: 0, audio: 0, document: 0 })
  const [assetGroups, setAssetGroups] = useState([])
  const [totalAssetGroups, setTotalAssetGroups] = useState(0)
  const [assetGroupsLoading] = useState(false)
  const [mediaLibraryFilterList, setMediaLibraryFilterList] = useState([])
  const [mediaLibraryOtherFilterList, setMediaLibraryOtherFilterList] = useState([])
  const [hasMediaLibraryTagFilter, setHasMediaLibraryTagFilter] = useState(false)

  // useAPI() builds a new client on every render; keep the latest one in a ref
  // so the memoized callbacks below stay stable across renders.
  const api = useAPI()
  const apiRef = useRef(api)
  apiRef.current = api

  const getMediaLibraryFilterList = useCallback(async () => {
    try {
      const response = await apiRef.current.get(`${filterUrl}/media-library`)
      const filters = response?.data?.data?.filters || []
      const otherFilters = response?.data?.data?.otherFilters || []

      const tagsFilter = filters.find(f => f.key === 'tags')
      setHasMediaLibraryTagFilter(!!tagsFilter)
      setMediaLibraryFilterList(tagsFilter ? tagsFilter.options.map(o => o.value) : [])
      setMediaLibraryOtherFilterList(otherFilters)

      const initialSelectedFilters = {}
      otherFilters.forEach(f => {
        initialSelectedFilters[f.key] = []
      })

      return initialSelectedFilters
    } catch {
      setMediaLibraryFilterList([])
      setMediaLibraryOtherFilterList([])
      setHasMediaLibraryTagFilter(false)

      return {}
    }
  }, [])

  const getMediaList = useCallback(async payload => {
    setLoading(true)
    try {
      const response = await apiRef.current.post(`${mediaAssetGroupsUrl}/search`, payload)
      setMediaListData(response?.data?.results ?? [])
      setTotalMedia(response?.data?.total ?? 0)

      setMediaTypeCounts(response?.data?.typeCounts ?? { image: 0, video: 0, audio: 0, document: 0 })
    } catch (error) {
      setMediaListData([])
      setTotalMedia(0)

      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  const updateMediaLibrary = async (assetGroupId, formPayload) => {
    const response = await api.put(
      `${mediaAssetGroupsUrl}/${assetGroupId}`,

      formPayload,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    )

    return response?.data
  }

  const uploadMediaLibrary = async ({ assets, groupMeta }) => {
    const formData = new FormData()

    // groupMeta JSON blob
    formData.append(
      'groupMeta',
      JSON.stringify({
        name: groupMeta.name,
        description: groupMeta.description || '',
        expirationDate: groupMeta.expirationDate || null,
        language: groupMeta.language || '',
        mediaType: groupMeta.mediaType || 'image',
        tags: groupMeta.tags || []
      })
    )

    // Build manifest — group same files by identity (name + size)
    const fileMap = new Map()
    assets.forEach(asset => {
      const fileKey = `${asset.file.name}_${asset.file.size}`
      if (fileMap.has(fileKey)) {
        fileMap.get(fileKey).channels.push(...asset.channels)
      } else {
        fileMap.set(fileKey, {
          fileField: `file_${fileMap.size}`,
          file: asset.file,
          name: asset.name,
          slot: asset.slot,
          channels: [...asset.channels],
          tags: asset.tags || []
        })
      }
    })

    const entries = [...fileMap.values()]

    const manifest = entries.map(entry => ({
      fileField: entry.fileField,
      name: entry.name,
      slot: entry.slot,
      language: groupMeta.language || '',
      channels: entry.channels,
      tags: entry.tags
    }))

    formData.append('manifest', JSON.stringify(manifest))

    // Append actual files
    entries.forEach(entry => {
      formData.append(entry.fileField, entry.file)
    })

    const response = await api.post(`${mediaAssetGroupsUrl}/uploads/library`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })

    return response?.data
  }

  const bulkAssetGroupAction = async (assetGroupIds, action) => {
    const response = await api.post(`${mediaAssetGroupsUrl}/bulk`, {
      action,
      assetGroupIds
    })

    return response?.data
  }

  const getMediaGroup = useCallback(async assetGroupId => {
    const response = await apiRef.current.get(`${mediaAssetGroupsUrl}/${assetGroupId}`)
    setSelectedMedia(response?.data)

    return response?.data
  }, [])

  const downloadAssetGroup = useCallback(async assetGroupId => {
    const response = await apiRef.current.get(`${mediaAssetGroupsUrl}/${assetGroupId}/download`, {
      responseType: 'blob'
    })

    const disposition = response?.headers?.['content-disposition'] || ''
    const match = disposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i)
    const fileName = match ? decodeURIComponent(match[1].replace(/"/g, '')) : `${assetGroupId}.zip`

    return { blob: response?.data, fileName }
  }, [])

  const deleteMedia = useCallback(async assetGroupId => {
    const response = await apiRef.current.delete(`${mediaAssetGroupsUrl}/${assetGroupId}`)
    setMediaListData(prev => prev.filter(m => m.assetGroupId !== assetGroupId))
    setTotalMedia(prev => prev - 1)

    return response?.data
  }, [])

  const archiveMedia = useCallback(async assetGroupId => {
    const response = await apiRef.current.post(`${mediaAssetGroupsUrl}/${assetGroupId}/archive`)
    setMediaListData(prev => prev.map(m => (m.assetGroupId === assetGroupId ? { ...m, status: 'archived' } : m)))

    return response?.data
  }, [])

  const restoreMedia = useCallback(async assetGroupId => {
    const response = await apiRef.current.post(`${mediaAssetGroupsUrl}/${assetGroupId}/unarchive`)
    setMediaListData(prev => prev.map(m => (m.assetGroupId === assetGroupId ? { ...m, status: 'active' } : m)))

    return response?.data
  }, [])

  const cloneMedia = useCallback(async (assetGroupId, name) => {
    const response = await apiRef.current.post(
      `${mediaAssetGroupsUrl}/${assetGroupId}/clone`,
      { name },
      { headers: { 'Content-Type': 'application/json' } }
    )

    // Add cloned group to top of list
    setMediaListData(prev => [response?.data, ...prev])
    setTotalMedia(prev => prev + 1)

    return response?.data
  }, [])

  const resetMedia = useCallback(() => {
    setSelectedMedia(null)
    setMediaListData([])
    setTotalMedia(0)
    setMediaTypeCounts({ image: 0, video: 0, audio: 0, document: 0 })
  }, [])

  const value = {
    mediaListData,
    setMediaListData,
    selectedMedia,
    setSelectedMedia,
    loading,
    setLoading,
    totalMedia,
    setTotalMedia,
    mediaTypeCounts,
    getMediaList,
    getMediaGroup,
    uploadMediaLibrary,
    updateMediaLibrary,
    deleteMedia,
    downloadAssetGroup,
    resetMedia,
    assetGroups,
    setAssetGroups,
    totalAssetGroups,
    setTotalAssetGroups,
    assetGroupsLoading,
    archiveMedia,
    restoreMedia,
    cloneMedia,
    bulkAssetGroupAction,
    mediaLibraryFilterList,
    mediaLibraryOtherFilterList,
    hasMediaLibraryTagFilter,
    getMediaLibraryFilterList
  }

  return <MediaLibraryContext.Provider value={value}>{children}</MediaLibraryContext.Provider>
}

export default MediaLibraryContext
